import {
  Button,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from '@dls/react-core';
import { Icon } from '@dls/react-icon';
import React, { useContext, useState } from 'react';
import useRequest from '../../../api/useRequest';
import { StudyPageContext } from '../../../App';
import { CreateUpdateConfigurationDialog } from '../../../components/dialogs/create-update-configuration-dialog';
import {
  EnhancedTableHead,
  HeadCell,
  Order,
  sortedTableStyles,
} from '../../../components/sorted-table/sorted-table-head';
import { getComparator, stableSort } from '../../../components/sorted-table/sorting-utils';
import { StyledBlueHeading } from '../../../components/styled/styles';
import { Configuration } from '../../../interfaces/configuration';
import { ColorDLS, ColorX11 } from '../../../styling/color';
import { ConfigurationWithId, getConfigurationsForStudyRequest } from '../../../api/studykit';
import { RemoveConfigurationDialog } from '../../../components/dialogs/remove-configuration-dialog';
import { HasPermission, usePermission } from '../../../context/authorization';
import cronstrue from 'cronstrue';
import { Typography } from '@material-ui/core';

const headCells: HeadCell<Configuration>[] = [
  {
    label: 'Name',
    type: 'sortable',
    numeric: false,
    disablePadding: true,
    id: 'name',
  },
  {
    label: 'Algorithm',
    type: 'sortable',
    numeric: false,
    disablePadding: true,
    id: 'algorithm',
  },
  {
    label: 'Scheduling',
    type: 'sortable',
    numeric: false,
    disablePadding: true,
    id: 'trigger',
  },
  {
    label: '',
    type: 'other',
    disablePadding: true,
    id: '',
  },
];

export const Configurations = () => {
  const { study } = useContext(StudyPageContext);
  const tableClasses = sortedTableStyles();
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Configuration>('name');
  const [createConfigurationDialogOpen, setCreateConfigurationDialogOpen] = useState(false);
  const [removeConfigurationDialogOpen, setRemoveConfigurationDialogOpen] = useState(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState<
    ConfigurationWithId | undefined
  >(undefined);

  const { data, mutate } = useRequest(getConfigurationsForStudyRequest(study.id));
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Configuration) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const canEdit = usePermission('Configurations.Update');
  const canDelete = usePermission('Configurations.Delete');

  return (
    <Paper className={'dlsTable'}>
      {selectedConfiguration && (
        <RemoveConfigurationDialog
          studyId={study.id}
          open={removeConfigurationDialogOpen}
          configuration={selectedConfiguration}
          onClose={() => setRemoveConfigurationDialogOpen(false)}
          mutate={mutate}
        />
      )}
      {createConfigurationDialogOpen && (
        <CreateUpdateConfigurationDialog
          open={true}
          configuration={selectedConfiguration}
          onClose={() => {
            setCreateConfigurationDialogOpen(false);
            mutate();
          }}
          studyId={study.id}
        />
      )}
      <div
        style={{
          display: `flex`,
          justifyContent: `space-between`,
          alignItems: `center`,
          padding: '32px 24px 24px 24px',
        }}
      >
        <StyledBlueHeading variant="h2">Configurations</StyledBlueHeading>
        <HasPermission value="Configurations.Create">
          <Button
            onClick={() => {
              setSelectedConfiguration(undefined);
              setCreateConfigurationDialogOpen(true);
            }}
            startIcon={<Icon name={'Plus'} color={ColorX11.White} size={'m'} />}
            style={{ width: `14em` }}
            dls_variant={'primary'}
          >
            Add configuration
          </Button>
        </HasPermission>
      </div>
      <div style={{ paddingBottom: '32px' }}>
        <TableContainer style={{ width: `unset` }}>
          <Table>
            <EnhancedTableHead
              classes={tableClasses}
              onRequestSort={handleRequestSort}
              order={order}
              orderBy={orderBy}
              headCells={headCells}
            />
            <TableBody className={'collapsible-table'}>
              {!data ? (
                <TableRow>
                  <TableCell colSpan={headCells.length}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : data.length > 0 ? (
                stableSort(data, getComparator(order, orderBy)).map((row) => {
                  return (
                    <SingleRow
                      key={row.id}
                      configuration={row}
                      setSelectedConfiguration={setSelectedConfiguration}
                      setOpenRemoveConfigurationDialog={setRemoveConfigurationDialogOpen}
                      setCreateConfigurationDialogOpen={setCreateConfigurationDialogOpen}
                      canEdit={canEdit}
                      canDelete={canDelete}
                    />
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={headCells.length} style={{ textAlign: 'center' }}>
                    There are no configurations assigned to this study yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Paper>
  );
};
interface RowProps {
  configuration: ConfigurationWithId;
  setSelectedConfiguration: React.Dispatch<React.SetStateAction<ConfigurationWithId | undefined>>;
  setOpenRemoveConfigurationDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setCreateConfigurationDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  canEdit: boolean;
  canDelete: boolean;
}

const SingleRow: React.FC<RowProps> = ({
  configuration,
  setSelectedConfiguration,
  setOpenRemoveConfigurationDialog,
  setCreateConfigurationDialogOpen,
  canEdit,
  canDelete,
}) => (
  <React.Fragment>
    <TableRow key={configuration.id} data-testid={'row-' + configuration.id}>
      <TableCell scope="row" data-testid={'configuration-' + configuration.id}>
        {configuration.name}
      </TableCell>
      <TableCell scope="row" data-testid={'configuration-' + configuration.id}>
        {`${configuration.algorithm.name} (${configuration.algorithm.version})`}
      </TableCell>
      <TableCell scope="row" data-testid={'configuration-' + configuration.id}>
        {configuration.trigger.type === 'manual' ? (
          'Manual'
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography>Cron expression </Typography>
            <CronExpression cron={configuration.trigger.params.cron} />
          </div>
        )}
      </TableCell>
      <TableCell
        scope="row"
        data-testid={'configuration-' + configuration.id}
        style={{ width: '8em' }}
      >
        <div
          style={{
            width: '100%',
            display: `flex`,
            justifyContent: `space-between`,
            alignContent: `center`,
          }}
        >
          {canEdit && (
            <Button
              dls_variant="quietEmphasis"
              onClick={() => {
                setSelectedConfiguration(configuration);
                setCreateConfigurationDialogOpen(true);
              }}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              dls_variant="quietEmphasis"
              onClick={() => {
                setSelectedConfiguration(configuration);
                setOpenRemoveConfigurationDialog(true);
              }}
            >
              Delete
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  </React.Fragment>
);

const CronExpression = ({ cron }: { cron: string }) => {
  return (
    <Tooltip arrow title={cronstrue.toString(cron, { verbose: true })}>
      <code
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          background: ColorDLS.Blue10,
          padding: '4px 8px',
        }}
      >
        {cron}
        <Icon size={'s'} name="InformationCircleOutline" />
      </code>
    </Tooltip>
  );
};
